import { format } from 'date-fns'

import type {
  AppointmentRecord,
  AvailabilityBlock,
} from '@/features/appointments/types'
import { parseAppointmentDate } from './parseAppointmentDate'

export type AppointmentsView = 'table' | 'calendar'

const OPEN_SLOT_ID_PREFIX = 'open-slot:'
export const OPEN_SLOT_STATUS = 'open'

/**
 * Resolve the Slots toggle (a boolean URL param) for a view: an explicit
 * on/off wins; unset falls back to the view default — shown in Calendar,
 * hidden in Table.
 */
export function resolveShowOpenSlots(
  value: boolean | null | undefined,
  view: AppointmentsView
): boolean {
  return value ?? view === 'calendar'
}

/** True for a table row synthesized from an open-availability block. */
export function isOpenSlotRow(row: AppointmentRecord): boolean {
  return row.id.startsWith(OPEN_SLOT_ID_PREFIX)
}

function openSlotToRow(block: AvailabilityBlock): AppointmentRecord {
  const start = parseAppointmentDate(block.block_start)
  return {
    // Interval identity stays stable even when display metadata changes.
    id: block.appointment_interval_id
      ? `${OPEN_SLOT_ID_PREFIX}interval:${block.appointment_interval_id}`
      : `${OPEN_SLOT_ID_PREFIX}${[
          block.provider_id,
          block.facility_name,
          block.appointment_type,
          block.block_start,
        ].join('|')}`,
    created_at: null,
    start_time: block.block_start || null,
    end_time: block.block_end || null,
    provider_name: block.provider_name || null,
    org_id: null,
    duty_id: null,
    status: OPEN_SLOT_STATUS,
    patient_id: null,
    patient_name: null,
    org_slug: null,
    provider_id: block.provider_id || null,
    facility_id: null,
    timeslot_id: null,
    source_appointment_id: null,
    appointment_type: block.appointment_type,
    appointment_reason: null,
    source_status: null,
    source_name: null,
    location: block.facility_name,
    source_appointment_date: start ? format(start, 'yyyy-MM-dd') : null,
    mrn: null,
    source_patient_id: null,
    additional_info: {
      timeslot_count: block.timeslot_count,
      ...(block.appointment_interval_id
        ? {
            appointment_interval_id: block.appointment_interval_id,
            available_capacity: block.available_capacity,
          }
        : {}),
    },
  }
}

function timeOf(row: AppointmentRecord): number | null {
  return parseAppointmentDate(row.start_time)?.getTime() ?? null
}

/**
 * Whether an open-slot row sorts above an appointment in the table's
 * newest-first order: a later day, or the same day at or after its time.
 * Appointments with no date give no position to compare against.
 */
function slotComesFirst(
  slot: AppointmentRecord,
  appointment: AppointmentRecord
) {
  const slotDay = slot.source_appointment_date
  const appointmentDay = appointment.source_appointment_date
  if (!slotDay || !appointmentDay) return false
  if (slotDay !== appointmentDay) return slotDay > appointmentDay
  const appointmentTime = timeOf(appointment)
  return appointmentTime === null || (timeOf(slot) ?? 0) >= appointmentTime
}

/**
 * Interleave open-availability blocks into the table's loaded appointments
 * (newest first), keeping the server's appointment order and available capacity intact.
 *
 * Slots come from their own endpoint, bounded by their own date window, so
 * every fetched slot is always rendered (as the calendar does): slots older
 * than every loaded appointment go at the end, and move into place as more
 * appointment pages load. Holding them back instead hid them entirely
 * whenever future bookings filled the first page.
 */
export function mergeOpenSlotRows(
  appointments: AppointmentRecord[],
  openSlots: AvailabilityBlock[]
): AppointmentRecord[] {
  if (openSlots.length === 0) return appointments

  const slotRows = openSlots
    .map(openSlotToRow)
    .toSorted((a, b) => (timeOf(b) ?? 0) - (timeOf(a) ?? 0))

  const merged: AppointmentRecord[] = []
  let next = 0
  for (const appointment of appointments) {
    while (
      next < slotRows.length &&
      slotComesFirst(slotRows[next]!, appointment)
    ) {
      merged.push(slotRows[next++]!)
    }
    merged.push(appointment)
  }
  merged.push(...slotRows.slice(next))
  return merged
}
